import { core, type Json } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
import { jsonParse } from '../utils/parse.js';

/**
 * 网络请求的输入报文
 */
export type FabricHttpRequestOptions<T extends Json> = {
  /**
   * 开发者服务器接口地址
   */
  url: string;
  /**
   * 请求的参数, object, GET请求, 则自动拼接到URL query, POST则post body发送服务器
   */
  data?: T;
  /**
   * HTTP 请求方法
   * @default 'GET'
   */
  method: 'GET' | 'POST';
  /**
   * 设置请求的 header，header 中不能设置 Referer。content-type 默认为 application/json
   */
  headers?: Record<string, string>;
  /**
   * 超时时间，单位为秒second
   * @default 60 s
   */
  timeout?: number;
};

/**
 * 发送网络请求, 返回业务数据报文协议
 */
export type FabricHttpRequestCallbackData<T extends Json> = {
  /**
   * 开发者服务器返回的数据
   */
  data: T;
  /**
   * 开发者服务器返回的 HTTP Response Header
   */
  headers: Record<string, string>;
  /**
   * 开发者服务器返回的 HTTP 状态码
   */
  statusCode: number;
};

export type FabricHttpRequestApi = <
  Return extends Json = Json,
  Input extends Record<string, Json> = Record<string, Json>,
>(
  options: FabricHttpRequestOptions<Input>
) => Promise<FabricHttpRequestCallbackData<Return>>;

/**
 * fabricErrors.code枚举如下:
 * ```ts
 *  "ntv_parse_error":其他JSON处理, 等异常都属于parse_error
 *  "ntv_input_error":参数输入错误
 *  "0" :请求成功
 *  "1" :无网络
 *  "2" :网络错误
 *  "3" :网络请求超时, 通常如果接口指定了timeout参数.
 *  "4" :请求报文加密错误.
 *  "5" :返回报文解密错误.
 *  "999" :未知错误
 * ```
 */
export const request: FabricHttpRequestApi = <
  Return extends Json = Json,
  Input extends Record<string, Json> = Record<string, Json>,
>(
  options: FabricHttpRequestOptions<Input>
) => {
  return new Promise<FabricHttpRequestCallbackData<Return>>(
    (resolve, reject) => {
      core.call<
        'fabric_http.request',
        FabricHttpRequestOptions<string>,
        FabricHttpRequestCallbackData<string>
      >(
        'fabric_http.request',
        {
          ...options,
          data: JSON.stringify(options.data),
        },
        (result) => {
          const fabricErrors = filterFabricErrors(
            result.code,
            result.message,
            result.data
          );
          if (fabricErrors) {
            reject(fabricErrors);
          } else {
            resolve({
              ...result.data,
              data: jsonParse(result.data.data),
            });
          }
        }
      );
    }
  );
};
